import { useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment/min/moment-with-locales';
import { flexRender, RowData } from '@tanstack/react-table';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Flex,
  Box,
  Icon,
  Badge,
  Checkbox,
  Tooltip,
  Modal,
  ModalBody,
  ModalContent,
  Tag,
  Text,
  Divider,
} from '@chakra-ui/react';
import {
  formatDate,
  formatPhoneNumber,
  getReadableColor,
  isEmpty,
} from '../../../helpers';
import { TTableContainer } from '../TableContainer';
import { allIcons, CirlceIcon, NormalIcon } from '../../utils/Icon';
import { TState, useActions } from '../../../redux/store';
import Loader from '../../common/Loader';
import RSelect from '../../form/RSelect';
import { useCanI, useGetSelect } from '../../../redux/actions';
import { updateData } from '../../../api/tabsApi';
import Counter from '../../form/Counter';
import { useGetCorrectIndex } from '../../tabs/SubTabs';
import { momentTimezone } from '../../utils/dateUtils';

declare module '@tanstack/react-table' {
  // eslint-disable-next-line
  interface ColumnMeta<TData extends RowData, TValue> {
    _id: string;
  }
}

const TableCrm = ({
  parentRef,
  totalSize,
  rowVirtualizer,
  noResize,
  isPending,
  onEditClick,
  getHeaderGroups,
  rows,
  commsById,
}: TTableContainer & {
  noResize?: boolean;
  commsById?: Record<any, any[]>;
}) => {
  const actionsList = useSelector((state: TState) => state.actionsList);
  const noFilter = useSelector((state: TState) => state.noFilter);
  const isPlanning = useSelector((state: TState) => state.isPlanning);
  const user = useSelector((state: TState) => state.user);
  const theme = useSelector((state: TState) => state.theme);
  const currentTab = useSelector((state: TState) => state.currentTab);
  const isAdmin = useSelector((state: TState) => state.isAdmin);
  const isDuplicate = useSelector(
    (state: TState) => state.currentTab?.isDuplicate,
  );

  const { getData, setModalData } = useActions();

  const filteredActionList = actionsList.filter((ac) => {
    const hasPage = !ac.pages || ac.pages.includes(`${currentTab?._id}-show`);
    const hasRole =
      isAdmin ||
      (ac.roles?.length &&
        user.role_id.some((id: string) => ac.roles?.includes(id)));

    return hasRole && hasPage;
  });

  const [
    { row: currRow, meta: currMeta, position: currPos, isAction },
    setIsContextMenu,
  ] = useState<{
    row?: any;
    meta?: any;
    position?: any;
    isAction?: boolean;
  }>({});

  const selectById = useGetSelect();
  const canI = useCanI();
  const getCorrectIndex = useGetCorrectIndex();

  const currentInput =
    currRow && selectById(currMeta._id, currRow.original[currMeta._id]);

  const badgeStyle = {
    px: 1.5,
    borderRadius: '5px',
    fontSize: theme.fontSize.xSmall,
  };

  const headerHeight = '54px';
  const isMulti = /multi/.test(currentInput?.type);
  const columnResizeDirection = 'ltr';

  const virtualRows = rowVirtualizer.getVirtualItems();

  const canHistory = canI('history', 'read');
  const canPlanning = canI('planning', 'read') && isPlanning;
  const actions = [
    { icon: 'HiChatBubbleLeftRight', tooltip: 'Commentaire' },
    { icon: 'HiDocumentText', tooltip: 'Documents' },
    canPlanning && { icon: 'HiCalendarDays', tooltip: 'RDV' },
    canHistory && { icon: 'HiClock', tooltip: 'Historique' },
    !user.noAction && { icon: 'HiBolt', tooltip: 'Actions rapide' },
  ];

  const isSafari = /apple/i.test(navigator.vendor);

  return noFilter ? (
    <Flex
      alignItems='center'
      bgColor={theme.lighter}
      w='fit-content'
      p={2}
      borderRadius={5}
      gap={2}
    >
      Aucune colonne défini dans l'onglet{' '}
      <Text fontWeight='bold'>Vue Principale</Text> pour cette page, voir le
      mode édition
    </Flex>
  ) : (
    <>
      <Modal
        isOpen={
          currRow &&
          (isAction ? filteredActionList.length : canI(currMeta?._id, 'update'))
        }
        onClose={() => {
          setIsContextMenu({});
        }}
        isCentered
      >
        <ModalContent
          border='1px solid lightgray'
          w='250px'
          position='absolute'
          style={{
            ...(isAction ? {} : { padding: 2 }),
            top: currPos?.top + 25,
            left: currPos?.left - 250 / 2 + currPos?.width / 2 - 5,
          }}
        >
          <Box className='triangle-top triangle-gray' />
          <Box className='triangle-top' />
          <ModalBody p={0}>
            {!isAction && (
              <RSelect
                isMulti={isMulti}
                defaultValue={currentInput?.currOption}
                options={currentInput?.options}
                onChange={async (e) => {
                  await updateData({
                    ...currRow.original,
                    [currMeta._id]: isMulti
                      ? e.length
                        ? e.map((v: any) => v.value)
                        : null
                      : (e?.value ?? null),
                  });
                  setIsContextMenu({});
                  await getData({ noRefresh: true });
                }}
                styles={{
                  control: (base: any) => ({
                    ...base,
                    borderColor: 'transparent',
                    boxShadow: 'none',
                    ':hover': {
                      borderColor: 'transparent',
                    },
                  }),
                }}
              />
            )}
            {isAction && (
              <Flex py={2} gap={1} flexWrap='wrap'>
                <Text fontSize={theme.fontSize.small} px={3}>
                  Actions
                </Text>
                <Divider mb={1} />
                <Flex px={3} gap={1} flexWrap='wrap'>
                  {filteredActionList.map((action) => (
                    <Tag
                      cursor='pointer'
                      bgGradient={`linear(to-br, ${theme.dark}, blue.300)`}
                      color='white'
                      onClick={async () => {
                        const data = action.list.reduce(
                          (prev, curr) => ({
                            ...prev,
                            [curr.name_id]:
                              curr.value === 'dateNow'
                                ? moment().toISOString()
                                : curr.value,
                          }),
                          {},
                        );
                        await updateData({
                          _id: currRow.original._id,
                          ...data,
                        });
                        getData();
                        setIsContextMenu({});
                      }}
                      position='relative'
                      _hover={{ top: 0.4, left: 0.4, boxShadow: 'md' }}
                      transition='ease .2s all'
                    >
                      {action.name}
                    </Tag>
                  ))}
                </Flex>
              </Flex>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
      {isPending && <Loader />}
      <Box
        ref={parentRef}
        id='reactTableContainer'
        overflow='auto'
        w='100%'
        borderRadius='5px'
        position='relative'
        whiteSpace='nowrap'
        bgColor={theme.none}
        opacity={isPending ? '0' : '1'}
        h='100%'
      >
        <Table w='max-content' id='reactTable' border='none'>
          <Thead h={headerHeight} position='sticky' top={0} left={0} zIndex={1}>
            {getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup?.id} h={headerHeight}>
                {headerGroup.headers.map((header) => {
                  const isPinnedLeft = header.column.getIsPinned() === 'left';
                  const isPinnedRight = header.column.getIsPinned() === 'right';
                  return (
                    <Th
                      key={header.column.columnDef.meta?._id}
                      p={0}
                      colSpan={header.colSpan}
                      style={{
                        minWidth: `${header.column.columnDef.minSize}px`,
                        width: `${header.getSize()}px`,
                        maxWidth: `${header.getSize()}px`,
                        position:
                          !isSafari && (isPinnedLeft || isPinnedRight)
                            ? 'sticky'
                            : 'static',
                        left: isPinnedLeft ? 0 : undefined,
                        right: isPinnedRight ? 0 : undefined,
                        background:
                          isPinnedLeft || isPinnedRight ? '#fff' : undefined,
                        zIndex: 2,
                      }}
                      transition='width 0.3s ease'
                      backgroundColor={theme.none}
                      color={theme.dark}
                      fontSize={theme.fontSize.table}
                      fontWeight='bold'
                    >
                      <Box
                        position={'relative'}
                        borderLeft={
                          (header.column.columnDef.meta as any)?.cellDisplay
                            ? '1px solid'
                            : ''
                        }
                        borderColor='gray.100'
                      >
                        {noResize ? null : (
                          <div
                            {...{
                              onDoubleClick: () => header.column.resetSize(),
                              onMouseDown: header.getResizeHandler(),
                              onTouchStart: header.getResizeHandler(),
                              className: `resizer ${columnResizeDirection} ${
                                header.column.getIsResizing()
                                  ? 'isResizing'
                                  : ''
                              }`,
                              cursor: 'ew-resize',
                            }}
                          />
                        )}
                        <Flex
                          alignItems='center'
                          direction='row'
                          borderBottomWidth='1px'
                          borderRightWidth='1px'
                          p={1.5}
                          h={headerHeight}
                          textAlign='center'
                        >
                          <Box
                            flexGrow={1}
                            onClick={header.column.getToggleSortingHandler()}
                            cursor='pointer'
                            my={1}
                            whiteSpace='wrap'
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext(),
                                )}
                            {
                              {
                                asc: <Icon as={ChevronUpIcon} />,
                                desc: <Icon as={ChevronDownIcon} />,
                              }[header.column.getIsSorted() as string]
                            }
                          </Box>
                        </Flex>
                      </Box>
                    </Th>
                  );
                })}
              </Tr>
            ))}
          </Thead>
          <Tbody position='relative' h={`${totalSize}px`}>
            {virtualRows.map((virtualRow) => {
              const row = rows[virtualRow.index];
              return (
                row && (
                  <Tr
                    key={row.original._id}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: `${virtualRow.size}px`,
                      transform: `translateY(${virtualRow.start}px)`,
                    }}
                    backgroundColor={
                      virtualRow.index % 2 ? theme.none : theme.lighter
                    }
                    color={theme.main}
                    _hover={{
                      backgroundColor: theme.light,
                      cursor: 'pointer',
                    }}
                  >
                    {row?.getVisibleCells().map((cell) => {
                      const meta = cell.column.columnDef.meta as any;
                      const cellDisplay = meta.cellDisplay;
                      const originalRow = row.original || {};
                      const color = meta?.colors?.[originalRow[meta?.colorKey]];
                      const isColorPicker = meta?.colorpicker;
                      const isDropdown =
                        /user|select/.test(meta.type) && !isDuplicate;
                      const lastComm =
                        meta.comms && commsById?.[originalRow._id];

                      const handleClick = () => {
                        if (lastComm) {
                          onEditClick(row);
                          setModalData({
                            tabIndex: getCorrectIndex(0),
                            original: row.original,
                          });
                          return;
                        }

                        /tel|counter/.test(meta.type) || cellDisplay
                          ? () => {
                              // handleCall(`${cell.renderValue()}`);
                            }
                          : !meta.tableIndex
                            ? onEditClick(row)
                            : null;
                      };

                      const hasContextMenu =
                        !currentTab?.isSetting && !isDuplicate;

                      const handleContextMenu = (e: any) => {
                        e.preventDefault();

                        if (!hasContextMenu) return;

                        if (isDropdown) {
                          currentTab?._id &&
                            canI(currentTab?._id, 'update') &&
                            canI(meta._id, 'update') &&
                            setIsContextMenu({
                              row,
                              meta,
                              position: e.currentTarget.getBoundingClientRect(),
                            });
                        } else {
                          setIsContextMenu({
                            row,
                            meta,
                            position: e.currentTarget.getBoundingClientRect(),
                            isAction: true,
                          });
                        }
                      };

                      const isPinnedLeft = cell.column.getIsPinned() === 'left';
                      const isPinnedRight =
                        cell.column.getIsPinned() === 'right';

                      return (
                        <Td
                          p={0}
                          textOverflow={
                            !/select|users/.test(meta.type) ? 'ellipsis' : ''
                          }
                          key={cell?.id}
                          style={{
                            minWidth: `${cell.column.columnDef.minSize}px`,
                            width: `${cell.column.getSize()}px`,
                            maxWidth: `${cell.column.getSize()}px`,
                            position:
                              isPinnedLeft || isPinnedRight
                                ? !isSafari
                                  ? 'sticky'
                                  : 'static'
                                : 'relative',
                            left: isPinnedLeft ? 0 : undefined,
                            right: isPinnedRight ? 0 : undefined,
                            zIndex: isPinnedLeft || isPinnedRight ? 3 : 2,
                            height: `${virtualRow.size}px`,
                          }}
                          backgroundColor={
                            isPinnedLeft || isPinnedRight
                              ? virtualRow.index % 2
                                ? theme.none
                                : theme.lighter
                              : ''
                          }
                          transition='width 0.3s ease'
                          overflow={'hidden'}
                          fontWeight={color ? 'bold' : '450'}
                          textAlign='center'
                          cursor={
                            hasContextMenu
                              ? 'context-menu'
                              : meta.tableIndex || meta.type === 'tel'
                                ? 'default'
                                : 'pointer'
                          }
                          fontSize={theme.fontSize.table}
                          onClick={handleClick}
                          onContextMenu={handleContextMenu}
                        >
                          {lastComm ? (
                            <Tooltip
                              label={`${lastComm.user} - ${momentTimezone(
                                lastComm.date,
                              )
                                .fromNow()
                                .replace('Invalid date', '')}`}
                            >
                              <Box
                                overflow='auto'
                                width={`${cell.column.getSize()}px`}
                                px={1}
                                position='absolute'
                                top={0.5}
                                bottom={0.5}
                                left={0}
                                textAlign='left'
                                lineHeight='1.2em'
                                bgColor='#f9fafb'
                                borderRadius='3px'
                              >
                                <Box
                                  whiteSpace='wrap'
                                  dangerouslySetInnerHTML={{
                                    __html: lastComm.comment,
                                  }}
                                />
                              </Box>
                            </Tooltip>
                          ) : cellDisplay ? (
                            <Flex
                              justifyContent='left'
                              borderLeft={
                                cellDisplay ? '1.5px solid lightgray' : 'none'
                              }
                              borderColor='gray.100'
                              h={`${virtualRow.size}px`}
                              alignItems='center'
                              flexWrap='wrap'
                              py={1}
                              pl='4px'
                            >
                              {actions.map((action, idx) =>
                                action &&
                                (action.icon !== 'HiBolt' ||
                                  actionsList.length) ? (
                                  <Tooltip label={action.tooltip}>
                                    <span>
                                      <NormalIcon
                                        color={theme.dark}
                                        _hover={{ color: theme.dark }}
                                        as={allIcons[action.icon]}
                                        boxSize={5}
                                        onClick={(e) => {
                                          if (action.icon === 'HiBolt') {
                                            setIsContextMenu({
                                              row,
                                              meta,
                                              position: {
                                                top: e.currentTarget.getBoundingClientRect()
                                                  .top,
                                                right: 10,
                                              },
                                              isAction: true,
                                            });
                                          } else {
                                            onEditClick(row);
                                            setModalData({
                                              tabIndex: getCorrectIndex(idx),
                                            });
                                          }
                                        }}
                                      />
                                    </span>
                                  </Tooltip>
                                ) : null,
                              )}
                            </Flex>
                          ) : meta.isMulti ? (
                            (originalRow[meta.colorKey] || null)?.map(
                              (valIdx: number) => (
                                <Badge
                                  key={`${valIdx}`}
                                  {...badgeStyle}
                                  ml={1}
                                  backgroundColor={
                                    meta?.colors?.[valIdx] || 'auto'
                                  }
                                  color={
                                    meta?.colors?.[valIdx]
                                      ? getReadableColor(meta?.colors?.[valIdx])
                                      : 'black'
                                  }
                                >
                                  {meta.options[valIdx]}
                                </Badge>
                              ),
                            ) || '-'
                          ) : meta.showValidation ? (
                            <Flex justifyContent='center'>
                              {`${cell.renderValue()}` === 'non' && (
                                <Tooltip
                                  label='Adresse non trouvé'
                                  placement='left'
                                >
                                  <span>
                                    <CirlceIcon
                                      as={
                                        allIcons[
                                          `${cell.renderValue()}` === 'oui'
                                            ? 'HiCheck'
                                            : 'HiMiniExclamationTriangle'
                                        ]
                                      }
                                      color={
                                        `${cell.renderValue()}` === 'oui'
                                          ? 'auto'
                                          : 'orange.400'
                                      }
                                    />
                                  </span>
                                </Tooltip>
                              )}
                            </Flex>
                          ) : meta.tableIndex ? (
                            <Flex
                              borderRight='1.5px solid lightgray'
                              borderColor='gray.100'
                              h='100%'
                              alignItems='center'
                              justifyContent='center'
                            >
                              <Checkbox
                                mr={1}
                                isChecked={row.getIsSelected()}
                                onChange={row.getToggleSelectedHandler()}
                              />
                              {virtualRow.index + 1}
                            </Flex>
                          ) : color ? (
                            <Badge
                              {...badgeStyle}
                              backgroundColor={color || 'auto'}
                              color={color ? getReadableColor(color) : 'black'}
                            >{`${cell.renderValue() ?? '-'}`}</Badge>
                          ) : meta.datedCol && meta.isAgo ? (
                            momentTimezone(`${cell.renderValue()}`)
                              .fromNow()
                              .replace('Invalid date', '')
                          ) : meta?.type === 'date' ? (
                            formatDate(
                              `${cell.renderValue()}`,
                              false,
                              meta._id === 'created_at' ||
                                meta.datedCol ||
                                meta.showTime
                                ? 'DD/MM/YYYY HH:mm'
                                : '',
                            )
                          ) : meta?.type === 'tel' ? (
                            <a
                              href={`tel:+33${formatPhoneNumber(
                                `${cell.renderValue()}`,
                                true,
                              ).replace(/^0/, '')}`}
                            >
                              <Text color={theme.dark} fontWeight='500'>
                                {formatPhoneNumber(
                                  `${cell.renderValue()}`,
                                  true,
                                )}
                              </Text>
                            </a>
                          ) : meta?.type === 'switch' &&
                            `${cell.renderValue()}` === 'true' ? (
                            <CirlceIcon
                              as={allIcons.HiCheck}
                              bgColor='transparent'
                              m='auto'
                            />
                          ) : meta?.type === 'switch' &&
                            `${cell.renderValue()}` === 'false' ? (
                            '-'
                          ) : meta?.type === 'counter' ? (
                            <Counter
                              handleOnChange={(value) =>
                                updateData({
                                  _id: row.original._id,
                                  [meta._id]: value,
                                })
                              }
                              value={Number(cell.renderValue())}
                              f={{ name_id: meta._id }}
                              needUpdate
                            />
                          ) : meta?.type === 'number' && cell.renderValue() ? (
                            <Text>{`${cell.renderValue() || ''}${meta?.unity ? ` ${meta?.unity}` : ''}`}</Text>
                          ) : isEmpty(cell.renderValue()) ? (
                            '-'
                          ) : (
                            <Flex
                              className='multi-line-ellipsis'
                              h={`${virtualRow.size}px`}
                            >
                              {`${cell.renderValue()}`.length >
                              0.16 * cell.column.getSize() ? (
                                <Tooltip label={`${cell.renderValue()}`}>
                                  {`${cell.renderValue()}`.slice(
                                    0,
                                    0.16 * cell.column.getSize(),
                                  ) + '...'}
                                </Tooltip>
                              ) : (
                                `${cell.renderValue()}`
                              )}
                            </Flex>
                          )}
                          {cell.renderValue() != null && isColorPicker ? (
                            <Box
                              display='inline-block'
                              boxSize={4}
                              ml={3}
                              bgColor={`${cell.renderValue()}`}
                              borderRadius={3}
                            />
                          ) : (
                            ''
                          )}
                        </Td>
                      );
                    })}
                  </Tr>
                )
              );
            })}
          </Tbody>
        </Table>
      </Box>
    </>
  );
};

export default TableCrm;
